import { useCallback, useEffect, useRef, useState } from 'react';

import Slider from '@cartwhl/ui/atoms/Slider';
import Text from '@cartwhl/ui/base/Text';
import PauseIcon from '@cartwhl/ui/icons/PauseIcon';
import PlayIcon from '@cartwhl/ui/icons/PlayIcon';
import GoButton from '@cartwhl/ui/input/GoButton';

const PREVIEW_VIDEO_FPS = 30;

export default function AssetLibraryMediaPreviewVideo({
  src,
}: {
  src: string;
}) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentFrame, setCurrentFrame] = useState(0);
  const [totalFrames, setTotalFrames] = useState(0);

  const updateTotalFrames = useCallback((video: HTMLVideoElement) => {
    if (!Number.isFinite(video.duration) || video.duration <= 0) {
      return;
    }

    setTotalFrames(Math.max(1, Math.round(video.duration * PREVIEW_VIDEO_FPS)));
  }, []);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) {
      return;
    }

    video.muted = true;

    const handleLoadedMetadata = () => {
      updateTotalFrames(video);
    };
    const handleTimeUpdate = () => {
      setCurrentFrame(Math.floor(video.currentTime * PREVIEW_VIDEO_FPS));
    };
    const handlePlay = () => setIsPlaying(true);
    const handlePause = () => setIsPlaying(false);

    video.addEventListener('loadedmetadata', handleLoadedMetadata);
    video.addEventListener('timeupdate', handleTimeUpdate);
    video.addEventListener('play', handlePlay);
    video.addEventListener('pause', handlePause);
    video.addEventListener('ended', handlePause);

    if (video.readyState >= HTMLMediaElement.HAVE_METADATA) {
      updateTotalFrames(video);
      setCurrentFrame(Math.floor(video.currentTime * PREVIEW_VIDEO_FPS));
      setIsPlaying(!video.paused);
    }

    return () => {
      video.removeEventListener('loadedmetadata', handleLoadedMetadata);
      video.removeEventListener('timeupdate', handleTimeUpdate);
      video.removeEventListener('play', handlePlay);
      video.removeEventListener('pause', handlePause);
      video.removeEventListener('ended', handlePause);
    };
  }, [src, updateTotalFrames]);

  const togglePlay = useCallback(() => {
    const video = videoRef.current;
    if (!video) {
      return;
    }

    if (video.paused) {
      void video.play();
      return;
    }

    video.pause();
  }, []);

  const seekToFrame = useCallback(
    (frame1Based: number) => {
      const video = videoRef.current;
      if (!video || totalFrames <= 0) {
        return;
      }

      const frame0 = Math.max(0, Math.min(frame1Based - 1, totalFrames - 1));
      video.currentTime = frame0 / PREVIEW_VIDEO_FPS;
      setCurrentFrame(frame0);
    },
    [totalFrames],
  );

  return (
    <div className="relative h-full w-full">
      <video
        ref={videoRef}
        key={src}
        src={src}
        autoPlay
        muted
        playsInline
        crossOrigin="anonymous"
        className="h-full w-full object-contain"
      />
      <div className="pointer-events-none absolute right-3 bottom-3 left-3 z-10">
        <div className="pointer-events-auto flex min-w-0 items-center gap-2">
          <GoButton
            className="h-8 w-10 shrink-0 p-0"
            icon={
              isPlaying ? (
                <PauseIcon className="h-4 w-4" />
              ) : (
                <PlayIcon className="h-3 w-3" />
              )
            }
            onClick={togglePlay}
          />
          <div className="text-gray-90 relative flex h-full items-center">
            <Text
              as="div"
              variant="studio/body/medium/sm"
              className="text-gray-90 text-end opacity-60"
              style={{
                width:
                  totalFrames > 0
                    ? totalFrames.toString().length * 21
                    : undefined,
              }}
            >
              {totalFrames > 0 ? (
                <>
                  {currentFrame + 1}/{totalFrames}
                </>
              ) : null}
            </Text>
          </div>
          <div className="pointer-events-auto flex h-8 min-w-0 flex-1 items-center px-2 lg:px-0">
            <Slider
              currentValue={totalFrames > 0 ? currentFrame + 1 : 1}
              min={1}
              max={Math.max(1, totalFrames)}
              step={1}
              size="sm"
              trackColor="player"
              rangeColor="player"
              trackClassName="!h-[5px]"
              disabled={totalFrames <= 1}
              onChange={seekToFrame}
            />
          </div>
        </div>
      </div>
    </div>
  );
}
